/**
 * 🟥 CUADRO NEGRO 2026-07-02 — Fila de "verb chips" en el chat de Genio.
 *
 * Fase 2 Auto-Visual Creation — Entrada visual pública.
 * 9 pills clickeables que precargan el `example_prompt` oficial de cada
 * verbo en el input del chat. Al enviar, `useLyonChat` detecta el verbo y
 * abre el modal AutoVisualCreation.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  MousePointerClick, Layout, Table, LayoutGrid, FormInput,
  File, Menu, Sparkles, BarChart, Loader2,
} from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

// Etiqueta corta por verbo (más UI-friendly que el `verb` oficial).
const SHORT_LABEL = {
  button:  'Botón',
  card:    'Card',
  table:   'Tabla',
  layout:  'Layout',
  form:    'Form',
  page:    'Página',
  navbar:  'Navbar',
  hero:    'Hero',
  chart:   'Gráfico',
};

// Ícono lucide por verbo (matching el catálogo backend).
const VERB_ICONS = {
  button:  MousePointerClick,
  card:    Layout,
  table:   Table,
  layout:  LayoutGrid,
  form:    FormInput,
  page:    File,
  navbar:  Menu,
  hero:    Sparkles,
  chart:   BarChart,
};

// Fallback local — si el fetch al backend falla, mostramos los 9 chips igual
// con el example_prompt hardcodeado del PRD del Cuadro Negro.
const FALLBACK_VERBS = [
  { id: 'button',  verb: 'Agregar botón',           example_prompt: "Agregá un botón primary que diga 'Guardar cambios'" },
  { id: 'card',    verb: 'Crear card',              example_prompt: "Creá una card con título 'Ventas del mes' y descripción" },
  { id: 'table',   verb: 'Insertar tabla',          example_prompt: 'Insertá una tabla con 3 columnas: Nombre, Rol, Estado' },
  { id: 'layout',  verb: 'Crear layout responsive', example_prompt: 'Creá un grid responsive de 3 columnas con cards' },
  { id: 'form',    verb: 'Agregar formulario',      example_prompt: 'Agregá un formulario con email + password + botón Ingresar' },
  { id: 'page',    verb: 'Crear página nueva',      example_prompt: 'Creá una página nueva con navbar + hero + 3 cards' },
  { id: 'navbar',  verb: 'Agregar navbar',          example_prompt: 'Agregá un navbar con logo DamyxIA y 3 items' },
  { id: 'hero',    verb: 'Insertar sección hero',   example_prompt: "Insertá un hero con título 'Bienvenido a DamyxIA' y CTA" },
  { id: 'chart',   verb: 'Agregar gráfico',         example_prompt: 'Agregá un gráfico de barras con ventas por mes' },
];

export default function VerbChipsRow({ onSelectPrompt, disabled = false }) {
  const [verbs, setVerbs] = useState(FALLBACK_VERBS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/lyon/action-verbs`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled && Array.isArray(data?.verbs) && data.verbs.length > 0) {
          setVerbs(data.verbs);
        }
      } catch (e) {
        // Silent — usamos FALLBACK_VERBS.
        console.warn('[VerbChipsRow] fetch failed, using fallback:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClick = useCallback((verb) => {
    if (disabled) return;
    if (typeof onSelectPrompt === 'function') {
      onSelectPrompt(verb.example_prompt || `Creá ${verb.verb.toLowerCase()}`);
    }
  }, [disabled, onSelectPrompt]);

  return (
    <div
      className="px-3 pt-2 pb-1 flex items-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-700 border-t border-slate-800 bg-slate-900/40"
      data-testid="verb-chips-row"
      role="toolbar"
      aria-label="Verbos de creación visual"
    >
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-indigo-300 font-semibold flex-shrink-0 pr-1">
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
        Crear
      </span>
      {verbs.map((v) => {
        const Icon = VERB_ICONS[v.id] || Sparkles;
        const short = SHORT_LABEL[v.id] || v.verb;
        return (
          <button
            key={v.id}
            type="button"
            onClick={() => handleClick(v)}
            disabled={disabled}
            data-testid={`verb-chip-${v.id}`}
            title={v.example_prompt}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-slate-700 bg-slate-800/60 hover:border-indigo-400 hover:bg-indigo-900/30 hover:text-indigo-200 text-[11px] text-slate-300 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon className="w-3 h-3 text-indigo-300" />
            <span>+ {short}</span>
          </button>
        );
      })}
    </div>
  );
}
